import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    addDaysToDate,
    CallStatisticsFormProps,
    generateCsvDataOfCallStatistic,
    generateData,
    getDurationStringWithoutSeconds,
    getFullDayDifference,
    initialValues,
    MetricUnit,
    roundTimeTo30Seconds,
    useStyles
} from './CallStatistics.utils';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from '../../../store';
import {useTranslation} from 'react-i18next';
import {Grid} from '@material-ui/core';
import {useFormik} from 'formik';
import classNames from 'classnames';
import {ReduxState} from '../../../store/types';
import {SearchCallback} from "../CallHistory.utils";
import Loader from "../../../components/Loader/Loader";
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis} from 'recharts';
import SwitchWithLabel from '../../../components/SwitchWithLabel/SwitchWithLabel';
import dayjs from '../../../services/customDayJs';
import {
    convertToUserFormat,
    convertUserLocalTimeToUtc,
    convertUtcToUserLocalTime
} from '../../../utils/dateWithTimezoneConversion';
import {CallActivityMetricRequest, CallActivityMetrics, MetricFilter} from '../../../store/actions/calls/payloads';
import ParentSize from '@visx/responsive/lib/components/ParentSize';
import CallStatisticsTimeLapChart from './CallStatisticsTimeLapChart';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import {CloudDownload} from '@material-ui/icons';
import InfoBanner from '../../../components/InfoBanner/InfoBanner';
import fileDownload from 'js-file-download';
import {asString, generateCsv, mkConfig} from "export-to-csv";
import {ServiceFeature, ServiceFeatureName} from '../../../store/types/ServiceFeature';
import {YesNo} from '../../../store/types/CallScreening';
import CallStatisticsTooltip from './CallStatisticsTooltip';
import CallStatisticsFilters from './CallStatisticsFilters';
import CustomizedTooltip from '../../../components/Tooltip/Tooltip';

export const CallStatistics: React.VFC<SearchCallback> = ({callback}) => {

    const defaultDateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {call_activity_metric_list, call_activity_metric_list_to_compare, call_activity_metrics_period,
        call_activity_metrics_payload, call_activity_metrics_payload_to_compare} = useSelector(
        (state: ReduxState) => state.calls,
    );

    const isLoading = useSelector<ReduxState, boolean>(
        (state) => (state.calls?.isLoadingCallMetric ?? false)
            || (state.calls?.isLoadingCallMetricCompare ?? false),
    );

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_time_format || ''
    );

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );
    
    const service_features = useSelector<ReduxState, ServiceFeature[]>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.service_features ?? []
    );

    const isCallMetricsFeatureEnabled = useMemo(() => {
        return service_features.find((v: ServiceFeature) => v.name == ServiceFeatureName.CallCenterActivity && v.effective_flag_value == YesNo.Yes);
    }, [service_features]);

    const [outgoingShown, setOutgoingShown] = useState<boolean>(true);
    const [incomingShown, setIncomingShown] = useState<boolean>(true);

    useEffect(() => {
        getCallStatistic(initialValues);
        dispatch(actions.ringGroupsList.request({}));
    }, []);

    const metricUnits = useMemo(() => {
        const minuteDiff = call_activity_metrics_period != 0 && call_activity_metrics_payload?.date_from && call_activity_metrics_payload?.date_to
            ? Math.abs(dayjs(call_activity_metrics_payload?.date_from , defaultDateTimeFormat)
                .diff(dayjs(call_activity_metrics_payload?.date_to, defaultDateTimeFormat), 'minute')) + 1
            : 60 * 24;
        const ret: MetricUnit[] = [
            {label: "30" + t<string>('enums:callMetricUnit.day'), unit: 'day', value: 30,
                disabled: minuteDiff < (60 * 24 * 30)},
            {label: "1" + t<string>('enums:callMetricUnit.week'), unit: 'week', value: 1,
                disabled: minuteDiff < (60 * 24 * 7)
            },
            {label: "1" + t<string>('enums:callMetricUnit.day'), unit: 'day', value: 1, disabled: minuteDiff < (60 * 24)},
            {label: "1" + t<string>('enums:callMetricUnit.hour'), unit: 'hour', value: 1, disabled: (minuteDiff < 60) || (minuteDiff > 30 * 24 * 60)},
            {label: "30" + t<string>('enums:callMetricUnit.min'), unit: 'min', value: 30, disabled: minuteDiff > (20 * 24 * 60)},
        ];
        return ret.filter(e => !e.disabled);
    }, [call_activity_metrics_period, call_activity_metrics_payload]);

    const getCallStatistic = useCallback((form: CallStatisticsFormProps) => {
        
        const filters: MetricFilter[] = [];

        //@ts-ignore
        const extensionFilter = form.agent?.extension_id; 
        if(extensionFilter?.length) {            
            filters.push({
                name: "extension_id",
                value: extensionFilter
            });
        }

        const ringGroupFilter = form.ringGroup;
        if(ringGroupFilter) {
            filters.push({
                name: "group_id",
                value: ringGroupFilter
            });
        }

        const metricsArray: CallActivityMetricRequest[] = [];
        switch(form.metric)
        {
            case 'connected_calls':
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "total_calls.outgoing"
                });
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "total_calls.incoming"
                });
                break;
            case 'hold_time':
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "suspended_duration.outgoing"
                });
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "suspended_duration.incoming"
                });
                break;
            case 'wrapup_time':
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "wrapup_duration.outgoing"
                });
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "wrapup_duration.incoming"
                });
                break;
            case 'talk_time':
            default:
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "media_duration.outgoing"
                });
                metricsArray.push(
                {
                    aggregator_list: [
                        {
                            name: "sum",
                            unit: form.unit,
                            value: form.unitValue,
                        }
                    ],
                    filter_list: filters,
                    name: "media_duration.incoming"
                });
                break;
        }
    
        const requestBase: CallActivityMetrics = {
            date_from: form.fromDateFilter,
            date_to: form.toDateFilter,
            metric_list: metricsArray,
            metric: form.metric,
        };

        dispatch(actions.getCallActivityMetrics.request({
            payload: requestBase,
            period: form.period,
            callback: () => {
                setFieldValue('unit', form.unit);
                setFieldValue('unitValue', form.unitValue);
            }
        }));

        if(form.compareWithPrevious) {
            const requestCompare = {
                ...requestBase,
                date_from: form.fromDateCompareFilter,
                date_to: form.toDateCompareFilter,
            };
            dispatch(actions.getCallActivityMetricsToCompare.request(requestCompare));
        } else {
            dispatch(actions.getCallActivityMetricsToCompare.request(undefined));
        }
    }, []);

    const {
        values,
        handleSubmit,
        setFieldValue,
        errors,
        setFieldError,
    } = useFormik<CallStatisticsFormProps>({
        initialValues,
        onSubmit: (form) => {
            const requestFrom = {
                ...form,
                fromDateFilter: form.fromDateFilter
                    ? convertUserLocalTimeToUtc(form.fromDateFilter, timezoneOffset)
                    : undefined,
                toDateFilter: form.toDateFilter
                    ? convertUserLocalTimeToUtc(form.toDateFilter, timezoneOffset)
                    : undefined,
                fromDateCompareFilter: form.fromDateCompareFilter
                    ? convertUserLocalTimeToUtc(form.fromDateCompareFilter, timezoneOffset)
                    : undefined,
                toDateCompareFilter: form.toDateCompareFilter
                    ? convertUserLocalTimeToUtc(form.toDateCompareFilter, timezoneOffset)
                    : undefined,
            }
            if(call_activity_metrics_payload && call_activity_metrics_payload.date_from != requestFrom.fromDateFilter)
            {
                let unit: "week" | "day" | "hour" | "min" =  "hour";
                switch(form.period) {
                    case 0: //today
                    case 1: //yesterday
                        unit = 'hour';
                        break;
                    case 2: //last 7 days
                        unit = 'day';
                        break;
                    case 3: //last 14 days
                    case 4: //last 30 days
                        unit = 'week';
                        break;
                    case 5:
                        unit = 'day';
                        break;
                }
                requestFrom.unit = unit;
                requestFrom.unitValue = 1;
            }
            getCallStatistic(requestFrom);
        },
        enableReinitialize: true,
        validateOnChange: false,
    });

    const firstPeriodFormattedForUser = useMemo(() => {

        let dateFromFilter = '';
        let dateToFilter = '';
        let dateFromFormated = '';
        let dateToFormated = '';

        switch(values.period) {
            case 0: //Today
                dateFromFilter = dayjs.utc().startOf('day').format(defaultDateTimeFormat);
                dateToFilter = convertUtcToUserLocalTime(
                    dayjs.utc().format(defaultDateTimeFormat),
                    timezoneOffset,
                    defaultDateTimeFormat);
                break;
            case 1: //Yesterday
                dateFromFilter = dayjs.utc().startOf('day').add(-1, 'day').format(defaultDateTimeFormat);
                dateToFilter = dayjs.utc().startOf('day').add(-1, 'seconds').format(defaultDateTimeFormat)
                break;
            case 2: //Last 7 days
                dateFromFilter = dayjs.utc().startOf('day').add(-7, 'day').format(defaultDateTimeFormat);
                dateToFilter = dayjs.utc().startOf('day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 3: //Last 14 days
                dateFromFilter = dayjs.utc().startOf('day').add(-14, 'day').format(defaultDateTimeFormat);
                dateToFilter = dayjs.utc().startOf('day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 4: //Last 30 days
                dateFromFilter = dayjs.utc().startOf('day').add(-30, 'day').format(defaultDateTimeFormat);
                dateToFilter = dayjs.utc().startOf('day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 5: //Custom
                if(!values.fromDate || !values.toDate) {
                    return 'Select date';
                }
                dateFromFilter = values.fromDate;
                dateToFilter = values.toDate;
                break;
        }
        dateFromFormated = convertToUserFormat(
            dateFromFilter,
            userDateTimeFormat);
        dateToFormated = convertToUserFormat(
            dateToFilter,
            userDateTimeFormat);
        if(values.fromDateFilter !== dateFromFilter) {
            setFieldValue('fromDateFilter', dateFromFilter);
        }
        setFieldValue('dateFromFormated', dateFromFormated);
        if(values.toDateFilter !== dateToFilter) {
            setFieldValue('toDateFilter', dateToFilter);
        }
        setFieldValue('dateToFormated', dateToFormated);
        return dateFromFormated + ' – ' + dateToFormated;
    }, [values, userDateTimeFormat, timezoneOffset]);

    const dateCompareFormattedForUser = useMemo(() => {

        let dateFromCompareFilter = '';
        let dateToCompareFilter = '';
        let dateFromFormated = '';
        let dateToFormated = '';

        switch(values.period) {
            case 0: //Today -> Yesterday
                dateFromCompareFilter = dayjs.utc().startOf('day').add(-1, 'day').format(defaultDateTimeFormat);
                dateToCompareFilter = dayjs.utc().startOf('day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 1: //Yesterday -> A day before Yesterday
                dateFromCompareFilter = dayjs.utc().startOf('day').add(-2, 'day').format(defaultDateTimeFormat);
                dateToCompareFilter = dayjs.utc().startOf('day').add(-1, 'day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 2: //Last 7 days -> 7 days before last 7 days
                dateFromCompareFilter = dayjs.utc().startOf('day').add(-14, 'day').format(defaultDateTimeFormat);
                dateToCompareFilter = dayjs.utc().startOf('day').add(-7, 'day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 3: //Last 14 days -> 14 days before last 14 days
                dateFromCompareFilter = dayjs.utc().startOf('day').add(-28, 'day').format(defaultDateTimeFormat);
                dateToCompareFilter = dayjs.utc().startOf('day').add(-14, 'day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 4: //Last 30 days -> 30 days before last 14 days
                dateFromCompareFilter = dayjs.utc().startOf('day').add(-60, 'day').format(defaultDateTimeFormat);
                dateToCompareFilter = dayjs.utc().startOf('day').add(-30, 'day').add(-1, 'seconds').format(defaultDateTimeFormat);
                break;
            case 5: //Custom
                if(!values.fromDate || !values.toDate) {
                    return 'Select date';
                }
                const fullDiffDays = getFullDayDifference(values.toDate, values.fromDate, defaultDateTimeFormat);
                dateFromCompareFilter = addDaysToDate(values.fromDate, (-1 * fullDiffDays) - 1, defaultDateTimeFormat);
                dateToCompareFilter = addDaysToDate(values.toDate, (-1 * fullDiffDays) - 1, defaultDateTimeFormat);
                break;
        }
        dateFromFormated = convertUtcToUserLocalTime(
            dateFromCompareFilter,
            0,
            userDateTimeFormat);
        dateToFormated = convertUtcToUserLocalTime(
            dateToCompareFilter,
            0,
            userDateTimeFormat);
        if(values.fromDateCompareFilter !== dateFromCompareFilter) {
            setFieldValue('fromDateCompareFilter', dateFromCompareFilter);
        }
        if(values.toDateCompareFilter !== dateToCompareFilter) {
            setFieldValue('toDateCompareFilter', dateToCompareFilter);
        }
        return dateFromFormated + ' – ' + dateToFormated;
    }, [values, userDateTimeFormat, timezoneOffset]);

    const data = useMemo(() => {
        return generateData(call_activity_metric_list, call_activity_metric_list_to_compare, userDateTimeFormat, call_activity_metrics_payload, timezoneOffset);
    }, [call_activity_metric_list, call_activity_metric_list_to_compare, userDateTimeFormat, call_activity_metrics_payload, timezoneOffset]);

    const showTimeLap = useMemo(() => {
        return data?.length > 7;
    }, [data]);

    const [filteredData, setFilteredData] = useState(data);

    const onClickLegendItem = useCallback((clicked: 'incoming' | 'outgoing') => {
        switch(clicked) {
            case 'incoming':
                if(!outgoingShown) {
                    return;
                }
                setIncomingShown(!incomingShown);
                break;
            case 'outgoing':
                if(!incomingShown) {
                    return;
                }
                setOutgoingShown(!outgoingShown);
                break;
        }
    }, [incomingShown, outgoingShown, setIncomingShown, setOutgoingShown]);

    const CustomTooltip = useCallback((props: TooltipProps<string, string>) => {

        if (!props.active || !props.payload || !props.payload.length) 
            return null;

        return (<CallStatisticsTooltip
            props={props} 
            call_activity_metrics_payload={call_activity_metrics_payload}
            userDateTimeFormat={userDateTimeFormat}
            call_activity_metrics_payload_to_compare={call_activity_metrics_payload_to_compare}
            data={data}
            />
        );
    }, [data, call_activity_metrics_payload, userDateTimeFormat, call_activity_metrics_payload_to_compare]);

    const diagramMetric = useMemo(() => {
        return (showTimeLap ? filteredData : data)?.[0]?.metric ?? 'talk_time';
    }, [showTimeLap, filteredData, data]);

    const downloadFile = useCallback(
    () => {
        let fileNamePrefix = t('screens:calls.csvFilenamePrefix');
        switch(diagramMetric) {
            case 'connected_calls':
                fileNamePrefix = t('screens:calls.connectedCallsCsvFilenamePrefix');
                break;
            case 'hold_time':
                fileNamePrefix = t('screens:calls.holdTimeCsvFilenamePrefix');
                break;
            case 'wrapup_time':
                fileNamePrefix = t('screens:calls.wrapupTimeCsvFilenamePrefix');
                break;
        }

        const fileName = fileNamePrefix + firstPeriodFormattedForUser + '.csv'; 
        const csvConfig = mkConfig({ 
            useKeysAsHeaders: true,
            showColumnHeaders: false,
        });

        const csvData = generateCsvDataOfCallStatistic(t, values, call_activity_metrics_payload, data,
            incomingShown, outgoingShown, call_activity_metrics_payload_to_compare);
        const csvContent = generateCsv(csvConfig)(csvData);
        fileDownload(asString(csvContent), fileName);
    }, [firstPeriodFormattedForUser, values, call_activity_metrics_payload, data,
        incomingShown, outgoingShown, call_activity_metrics_payload_to_compare, diagramMetric
    ]);

    if(!isCallMetricsFeatureEnabled) {
        return <InfoBanner text={t('screens:calls.callStatisticsNotAvailable')} />;
    }

    return (<>
        <form
            onSubmit={handleSubmit}
            autoComplete="off"
            data-testid="call-statistics-form"
            className={classes.formContainer}
        >
            <Grid item className={classes.filtersContainer}>

                <CallStatisticsFilters
                    setFieldValue={setFieldValue}
                    errors={errors}
                    setFieldError={setFieldError}
                    values={values}
                    handleSubmit={handleSubmit}
                    callback={callback}
                />

                <Grid>
                    <SwitchWithLabel
                        label={t('screens:calls.compareWithPrevious')}
                        value={values.compareWithPrevious}
                        dataQa={'compare-with-previous'}
                        field={'compareWithPrevious'}
                        id={'compareWithPrevious'}
                        setValue={(_, value) => {
                            setFieldValue('compareWithPrevious', value);
                            setTimeout(() => {
                                handleSubmit();
                            }, 1);
                        }}
                        className={classes.compareWithPrevious}
                        customLabelClass={classes.compareWithPreviousLabel}
                        skipPermission
                    />
                </Grid>

                <Grid className={classes.dataContainer}>
                    {isLoading && (
                        <div className={classes.spinnerContainer}>
                            <Loader dataQa={'call-statistic-loader'}/>
                        </div>)
                    }

                    <div className={classes.datesContainer}>
                        <div className={classes.datesContainerText}>
                            <div className={classes.dateValue}>{firstPeriodFormattedForUser}</div>
                            {values.compareWithPrevious && (<div className={classes.vs}>{t('screens:calls.vs')}</div>)}
                            {values.compareWithPrevious && (<div className={classes.dateValue}>{dateCompareFormattedForUser}</div>)}
                        </div>
                        <div style={{marginRight: 2}}>
                            <CustomizedIconButton
                                tooltipText={t(
                                        'tooltips:calls.downloadMetricsCsv',
                                )}
                                dataQa="metrics-download-icon"
                                dataTestId="download-icon"
                                onClick={downloadFile}
                            >
                                <CloudDownload />
                            </CustomizedIconButton>
                        </div>
                    </div>

                    <div className={classes.chartContainer}>
                        <div className={classes.chartHeader}>
                            <div className={classes.chartTitleAndLegendContainer}>
                                <div className={classes.chartTitle}>
                                    {t('screens:dashboard.calls')}
                                </div>
                                <div className={classes.legend}>
                                    <CustomizedTooltip
                                        copy={false}
                                        title={incomingShown
                                            ? t('screens:calls.hide')
                                            : t('screens:calls.show')
                                        }
                                        data-qa={'incoming-tooltip'}
                                        disableHoverListener={!outgoingShown}
                                    >
                                        <div className={classes.legendCallType} onClick={() => {onClickLegendItem('incoming');}}>
                                            <div className={classNames(classes.legendIcon, classes.legendIconIncoming, !incomingShown && classes.legendIconInactive)}></div>
                                            <div className={classNames(classes.legendText, !incomingShown && classes.legendTextInactive)}>{t('enums:sipCallType.incoming')}</div>
                                        </div>
                                    </CustomizedTooltip>
                                    <CustomizedTooltip
                                        copy={false}
                                        title={outgoingShown
                                            ? t('screens:calls.hide')
                                            : t('screens:calls.show')
                                        }
                                        data-qa={'incoming-tooltip'}
                                        disableHoverListener={!incomingShown}
                                    >
                                        <div className={classes.legendCallType} onClick={() => {onClickLegendItem('outgoing');}}>
                                            <div className={classNames(classes.legendIcon, classes.legendIconOutgoing, !outgoingShown && classes.legendIconInactive)}></div>
                                            <div className={classNames(classes.legendText, !outgoingShown && classes.legendTextInactive)}>{t('enums:sipCallType.outgoing')}</div>
                                        </div>
                                    </CustomizedTooltip>
                                </div>
                            </div>
                            <div className={classes.unitsSelectorContainer}>
                                {metricUnits.map(mu => (<div
                                    className={classNames(classes.unitItem,  
                                        mu.value === values.unitValue && mu.unit === values.unit && classes.unitItemSelected)} 
                                    key={'metric_unit_' + mu.label}
                                    onClick={() => {
                                        setFieldValue('unit', mu.unit);
                                        setFieldValue('unitValue', mu.value);
                                        handleSubmit();
                                    }} style={{color: mu.disabled ? 'red' : undefined}}>
                                        {mu.label}
                                </div>))}
                            </div>
                        </div>
                        <ResponsiveContainer width={976} height={200} minWidth={976} minHeight={200}>
                            <BarChart
                                width={976}
                                height={200}
                                data={showTimeLap ? filteredData : data}
                                margin={{
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <Tooltip content={<CustomTooltip />} />
                                <CartesianGrid vertical={false} stroke="rgba(221, 225, 231, 1)" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'rgba(146, 146, 157, 1)', fontSize: 12}} />
                                <YAxis 
                                    axisLine={false} 
                                    tickLine={false}
                                    domain={[0, (dataMax: number) => diagramMetric == 'connected_calls'
                                        ? dataMax
                                        : (dataMax > 120 ? roundTimeTo30Seconds(dataMax) : 120)
                                    ]}
                                    tick={{ fill: 'rgba(146, 146, 157, 1)', fontSize: 12}} 
                                    tickFormatter={(value) => 
                                        diagramMetric == 'connected_calls'
                                        ? value + ''
                                        : getDurationStringWithoutSeconds(roundTimeTo30Seconds(value))
                                    }
                                />
                                {outgoingShown && (<Bar dataKey="outgoing" stackId="a" fill="rgba(81, 166, 237, 1)" radius={[4, 4, 4, 4]} barSize={14} stroke="#fff" strokeWidth={1} activeBar={false} />)}
                                {incomingShown && (<Bar dataKey="incoming" stackId="a" fill="rgba(245, 132, 31, 1)" radius={[4, 4, 4, 4]} barSize={14} stroke="#fff" strokeWidth={1} activeBar={false} />)}
                                
                                {values.compareWithPrevious && outgoingShown && (<Bar dataKey="outgoing_compare" stackId="b" fill="rgba(81, 166, 237, 0.5)" radius={[4, 4, 4, 4]} barSize={16} activeBar={false} />)}
                                {values.compareWithPrevious && incomingShown && (<Bar dataKey="incoming_compare"  stackId="b" fill="rgba(245, 132, 31, 0.5)" radius={[4, 4, 4, 4]} barSize={16} activeBar={false} />)}
                            </BarChart>
                        </ResponsiveContainer>
                        
                        {showTimeLap && (
                            <div className={classes.timelapContainer}>
                                <ParentSize>{({ width, height }) => <CallStatisticsTimeLapChart 
                                    width={width} 
                                    height={height} 
                                    data={data}
                                    setFilteredData={setFilteredData}
                                    outgoingShown={outgoingShown}
                                    incomingShown={incomingShown}
                                />}</ParentSize>
                            </div>
                        )}
                    </div>
                </Grid>
            </Grid>
        </form>
    </>);
};
